import { useEffect, useMemo, useState } from "react";

export type CommunicationChannel = {
  sms: boolean;
  mail: boolean;
};

export type CommunicationState = {
  sendBookingConfirmation: CommunicationChannel;
  sendConfirmationReminder: CommunicationChannel;
  sendSecondReminder: CommunicationChannel;
  sendAfterAppointmentNote: CommunicationChannel;
  sendAfterPaymentReceipt: CommunicationChannel;
  sendFollowupNote: CommunicationChannel;
  sendCheckInReminder: CommunicationChannel;
};

export type CommunicationConfig = {
  sendBookingConfirmationSms: boolean;
  sendBookingConfirmationMail: boolean;
  sendConfirmationReminderSms: boolean;
  sendConfirmationReminderMail: boolean;
  sendSecondReminderSms: boolean;
  sendSecondReminderMail: boolean;
  sendAfterAppointmentNoteSms: boolean;
  sendAfterAppointmentNoteMail: boolean;
  sendFollowupNoteSms: boolean;
  sendFollowupNoteMail: boolean;
  sendAfterPaymentReceiptSms: boolean;
  sendAfterPaymentReceiptMail: boolean;
  sendCheckInReminderSms: boolean;
  sendCheckInReminderMail: boolean;
};

export type ConfigurationToUpdate = {
  [key in keyof CommunicationState as `${key}Sms` | `${key}Mail`]: boolean;
};

export const defaultApptCommunicationState: CommunicationState = {
  sendBookingConfirmation: { sms: null, mail: null },
  sendConfirmationReminder: { sms: null, mail: null },
  sendSecondReminder: { sms: null, mail: null },
  sendAfterAppointmentNote: { sms: null, mail: null },
  sendFollowupNote: { sms: null, mail: null },
  sendAfterPaymentReceipt: { sms: null, mail: null },
  sendCheckInReminder: { sms: null, mail: null },
};

export default function useApptCustomizedCommunication(
  defaultConfiguration: Partial<CommunicationConfig>
) {
  const [apptCommunicationState, setApptCommunicationState] = useState(
    defaultApptCommunicationState
  );

  useEffect(() => {
    if (defaultConfiguration) {
      setApptCommunicationState({
        sendBookingConfirmation: {
          sms: defaultConfiguration.sendBookingConfirmationSms,
          mail: defaultConfiguration.sendBookingConfirmationMail,
        },
        sendConfirmationReminder: {
          sms: defaultConfiguration.sendConfirmationReminderSms,
          mail: defaultConfiguration.sendConfirmationReminderMail,
        },
        sendSecondReminder: {
          sms: defaultConfiguration.sendSecondReminderSms,
          mail: defaultConfiguration.sendSecondReminderMail,
        },
        sendAfterAppointmentNote: {
          sms: defaultConfiguration.sendAfterAppointmentNoteSms,
          mail: defaultConfiguration.sendAfterAppointmentNoteMail,
        },
        sendFollowupNote: {
          sms: defaultConfiguration.sendFollowupNoteSms,
          mail: defaultConfiguration.sendFollowupNoteMail,
        },
        sendAfterPaymentReceipt: {
          sms: defaultConfiguration.sendAfterPaymentReceiptSms,
          mail: defaultConfiguration.sendAfterPaymentReceiptMail,
        },
        sendCheckInReminder: {
          sms: defaultConfiguration.sendCheckInReminderSms,
          mail: defaultConfiguration.sendCheckInReminderMail,
        },
      });
    }
  }, [defaultConfiguration]);

  const communicationStatusText = useMemo(() => {
    // 2 channels (sms and mail) per type
    const totalChannels = Object.keys(apptCommunicationState).length * 2;
    let enabledChannels = 0;

    Object.values(apptCommunicationState).forEach((value) => {
      if (value.sms) enabledChannels++;
      if (value.mail) enabledChannels++;
    });

    if (enabledChannels === 0) {
      return "Not sending any communications";
    }
    if (enabledChannels === totalChannels) {
      return "Sending all client communication";
    }

    return "Sending some client communication";
  }, [apptCommunicationState]);

  // Returns only the properties that changed from the default configuration
  const configurationToUpdate = useMemo(
    () =>
      Object.entries(apptCommunicationState).reduce((acc, [key, value]) => {
        const smsKey = `${key}Sms`;
        const mailKey = `${key}Mail`;

        if (defaultConfiguration?.[smsKey] !== value.sms) {
          acc[smsKey] = value.sms;
        }

        if (defaultConfiguration?.[mailKey] !== value.mail) {
          acc[mailKey] = value.mail;
        }

        return acc;
      }, {} as ConfigurationToUpdate),
    [apptCommunicationState, defaultConfiguration]
  );

  return {
    apptCommunicationState,
    setApptCommunicationState,
    communicationStatusText,
    configurationToUpdate,
    loading: !defaultConfiguration,
  };
}
